/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import { useDispatch, useSelector } from 'react-redux'; // Importing hooks for Redux dispatch and selector
import {useEffect, useState, useRef} from 'react'; // Importing React hooks
import { useTranslation } from 'react-i18next'; // Importing i18n for translations
import {
    DeleteEmployeeByContractAndUserList,
    DeleteVehicleByContractAndVehicleList,
    GetContractByIdV2,
} from '../../../../reduxToolkit/Contractor/ContractorApi';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import { STATUS_MODEL } from '../../../../enums/statusEnum';

import { DeleteItemsApi } from '../../../../reduxToolkit/Commons/CommonsApi';
import routes from '../../../../Routes/routes'; // Importing API functions for contractor actions
import { CONTRACT_DETAILS_VIEW } from '../Model/Models';
import {calculateHeight, errorMsg} from '../../../../Apis/functions';
import { TABLES } from '../../../../Apis/Tables';

const ContractorDetailsApp = () => {
    const navigate = useNavigate(); // Initialize navigate function for routing
    const { t } = useTranslation(); // Translation hook to handle language translations
    const dispatch = useDispatch(); // Redux dispatch hook
    const { contractId } = useParams();
    const contractorId = sessionStorage.getItem('contractorId'); // Fetch contractorId from sessionStorage
    const location = useLocation();
    const { state } = location; // Get state from location
    const currentSubView = state?.currentSubView; // Retrieve contractor ID from the state passed via navigation

    // State variables
    const [contractDetail, setContractDetail] = useState();
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Accessing development data from Redux store
    const [selectEmployeeForDelete, setSelectEmployeeForDelete] = useState([]);

    const [selectVehicleForDelete, setSelectVehicleForDelete] = useState([]);
    const [deleteShowEmployee, setDeleteShowEmployee] = useState(false);
    const [deleteShowVehicle, setDeleteShowVehicle] = useState(false);
    const [deleteShowContract, setDeleteShowContract] = useState(false);
    const [addUserModal, setAddUserModal] = useState(false);
    const [addVehicleModal, setAddVehicleModal] = useState(false);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Accessing user permissions from Redux store
    const [modified, setModified] = useState(false);
    const createdDate = new Date(contractDetail?.startDate); // Formatting the contract start date
    const endDate = new Date(contractDetail?.endDate); // Formatting the contract end date
    const [isAllCheckedEmployee, setIsAllCheckedEmployee] = useState(false);
    const [isAllCheckedVehicle, setIsAllCheckedVehicle] = useState(false);
    let docID;
    const isFinished = contractDetail?.statusId === STATUS_MODEL.CONTRACT_FINISH;
    const [subView, setSubView] = useState(currentSubView || CONTRACT_DETAILS_VIEW.EMPLOYEE);

    const availableHeightColumnOneRef = useRef(null);
    const availableHeightColumnTwoRef = useRef(null);
    const containerHeightRef = useRef(null);
    const containerHeightCardsRef = useRef(null);

    useEffect(() => {
        calculateHeight({elementRef: availableHeightColumnOneRef, bottom:30});
        calculateHeight({elementRef: availableHeightColumnTwoRef, bottom:30});
        calculateHeight({elementRef: containerHeightRef, bottom:60});
        calculateHeight({elementRef: containerHeightCardsRef, bottom:60});
    }, [contractDetail]);

    const handleSubViewChange = (value) => {
        setSubView(value);
    };

    // Function to delete selected employees
    const deleteEmployee = () => {
        const body = {
            userIds: selectEmployeeForDelete,
            contractId
        };
        dispatch(DeleteEmployeeByContractAndUserList(body)).then(
            ({
                payload: {
                    data: { success, code },
                }
             }) => {
                if (success) {
                    setModified(true); // Mark data as modified
                }else{
                    errorMsg(code);
                }
            }
        );
    };

    // Function to delete selected vehicles
    const deleteVehicle = () => {
        const body = {
            vehicleIds: selectVehicleForDelete,
            contractId
        };
        dispatch(DeleteVehicleByContractAndVehicleList(body)).then(
            ({
                payload: {
                    data: { success, code },
                }
             }) => {
                if (success) {
                    setModified(true); // Mark data as modified
                }else{
                    errorMsg(code);
                }
            }
        );
    };

    // Function to delete selected contracts
    const deleteContract = (deleteItem) => {
        const tableName = TABLES.CONTRACTS; // Table name for contracts
        const body = deleteItem; // Data to be deleted

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName, body })).then(
                ({
                    payload: {
                        data: { success, code },
                    },
                }) => {
                    if (success) {
                        navigate(contractorId ? routes.contractor.contractList : routes.contractor.list);
                    } else {
                        errorMsg(code);
                    }
                }
            );
        }
    };

    // useEffect hook to load data when component mounts or updates
    useEffect(() => {
        // Fetching contract details
        getContractInfo();
    }, []); // Dependency array to trigger re-fetching on update

    useEffect(() => {
        // Fetching contract details
        if(modified){
            getContractInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    const getContractInfo = () => {
        dispatch(GetContractByIdV2(contractId)).then(
            ({
                 payload: {
                     data: { data, success },
                 }
             }) => {
                if (success) {
                    docID = data?.id;
                    setContractDetail(data); // Setting contract details in the state
                }
            }
        );
    };

    return {
        t,
        navigate,
        modified,
        setModified,
        DEV,
        permission,
        contractId,
        contractorId,
        deleteShowContract,
        setDeleteShowContract,
        contractDetail,
        setAddUserModal,
        selectEmployeeForDelete,
        deleteShowEmployee,
        setDeleteShowEmployee,
        deleteShowVehicle,
        setDeleteShowVehicle,
        setSelectVehicleForDelete,
        addUserModal,
        addVehicleModal,
        deleteEmployee,
        deleteVehicle,
        deleteContract,
        createdDate,
        endDate,
        setAddVehicleModal,
        selectVehicleForDelete,
        setSelectEmployeeForDelete,
        isAllCheckedEmployee,
        setIsAllCheckedEmployee,
        isAllCheckedVehicle,
        setIsAllCheckedVehicle,
        isFinished,
        subView,
        handleSubViewChange,
        availableHeightColumnOneRef,
        availableHeightColumnTwoRef,
        containerHeightRef,
        containerHeightCardsRef
    };
};

export default ContractorDetailsApp; // Exporting the component for use in other parts of the app
